import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import {
    ArrowLeft,
    Check,
    Calendar,
    Hash,
    FileText,
} from "lucide-react";
import { fetchProgram } from "../lib/api";
import SearchResultsNav from "../components/SearchResultsNav";

const TYPE_LABEL = {
    kommune: "Bergen kommune",
    fylkesting: "Vestland fylkesting",
    nasjonal: "Nasjonalt program (Stortinget)",
};

function periodStatus(period) {
    const m = period?.match(/(\d{4})\s*[–-]\s*(\d{4})/);
    if (!m) return "unknown";
    const start = parseInt(m[1], 10);
    const end = parseInt(m[2], 10);
    const y = new Date().getFullYear();
    if (y >= start && y <= end) return "current";
    if (y > end) return "historical";
    return "upcoming";
}

export default function Program() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [targetId, setTargetId] = useState(null);
    const contentRef = useRef(null);

    useEffect(() => {
        fetchProgram(id)
            .then(setData)
            .catch((e) => setError(e?.response?.data?.detail || "Kunne ikke laste program"));
    }, [id]);

    // Deep-link: scroll to and highlight target paragraph whenever the hash
    // changes (also when navigating between search hits inside the same
    // program). Re-runs every time the hash or the loaded data change.
    useEffect(() => {
        if (!data) return undefined;
        const hash = location.hash;
        if (!hash) {
            setTargetId(null);
            return undefined;
        }
        const pid = hash.replace(/^#p-/, "");
        const el = document.getElementById(`p-${pid}`);
        if (!el) return undefined;
        setTargetId(pid);
        const t = setTimeout(() => {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 60);
        return () => clearTimeout(t);
    }, [data, location.hash]);

    const tocItems = useMemo(() => {
        if (!data) return [];
        return data.paragraphs
            .filter((p) => !p.is_artifact && (p.kind === "heading" || p.is_heading))
            .slice(0, 120);
    }, [data]);

    const handleCopyLink = (pid) => {
        const url = `${window.location.origin}/programs/${id}#p-${pid}`;
        navigator.clipboard.writeText(url);
        setCopiedId(pid);
        setTimeout(() => setCopiedId(null), 1800);
    };

    if (error) {
        return (
            <main className="mx-auto max-w-3xl px-6 py-24 md:px-12">
                <div className="border border-red-300 bg-red-50 p-6 text-red-900">
                    {error}
                </div>
                <button
                    onClick={() => navigate("/")}
                    className="pp-btn-secondary mt-6"
                >
                    Tilbake
                </button>
            </main>
        );
    }
    if (!data) {
        return (
            <main className="mx-auto max-w-7xl px-6 py-16 md:px-12">
                <div data-testid="program-loading" className="font-mono text-sm text-zinc-500">
                    Laster program…
                </div>
            </main>
        );
    }

    const { program, paragraphs } = data;

    return (
        <main data-testid="program-page" className="mx-auto max-w-7xl px-6 py-10 md:px-12">
            <SearchResultsNav />
            <Link
                to="/"
                data-testid="back-to-home"
                className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-950"
            >
                <ArrowLeft className="h-4 w-4" /> Alle programmer
            </Link>

            <header className="mb-10 border-b border-zinc-200 pb-8">
                <div className="pp-overline mb-3 flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-1.5 border border-[#C8102E] bg-[#C8102E] px-2 py-0.5 text-white">
                        Rødt
                    </span>
                    <span>{TYPE_LABEL[program.type] || program.type}</span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> {program.period}
                    </span>
                    {periodStatus(program.period) === "current" && (
                        <span className="inline-flex items-center gap-1 border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-emerald-800">
                            Gjeldende
                        </span>
                    )}
                    {periodStatus(program.period) === "historical" && (
                        <span className="inline-flex items-center gap-1 border border-zinc-300 bg-zinc-100 px-2 py-0.5 text-zinc-700">
                            Historisk
                        </span>
                    )}
                </div>
                <h1
                    data-testid="program-title"
                    className="font-heading text-4xl font-black tracking-tighter text-zinc-950 md:text-6xl"
                >
                    {program.region ||
                        (program.type === "kommune"
                            ? "Bergen"
                            : program.type === "fylkesting"
                              ? "Vestland"
                              : "Norge")}
                </h1>
                <p className="mt-2 font-heading text-lg text-zinc-500 md:text-xl">
                    Partiprogram {program.period}
                </p>
                <p className="mt-4 inline-flex items-center gap-2 text-sm text-zinc-500">
                    <FileText className="h-4 w-4" />
                    {program.original_filename} · {program.num_pages} sider ·{" "}
                    {program.num_paragraphs} avsnitt
                </p>
            </header>

            <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
                {/* Sticky sidebar: keywords + TOC */}
                <aside className="md:col-span-4 lg:col-span-3">
                    <div className="md:sticky md:top-24">
                        {program.keywords?.length > 0 && (
                            <section className="mb-8">
                                <h2 className="pp-overline mb-3">Nøkkelord</h2>
                                <div className="flex flex-wrap gap-1.5">
                                    {program.keywords.map((kw) => (
                                        <KeywordPill key={kw} keyword={kw} />
                                    ))}
                                </div>
                            </section>
                        )}

                        {program.topics?.length > 0 && (
                            <section className="mb-8">
                                <h2 className="pp-overline mb-3">Hovedtemaer</h2>
                                <ul className="space-y-3">
                                    {program.topics.map((t, i) => (
                                        <li
                                            key={`${i}-${t.title}`}
                                            className="border-l-2 border-[#C8102E] pl-3"
                                        >
                                            <div className="font-heading text-sm font-bold text-zinc-950">
                                                {t.title}
                                            </div>
                                            {t.summary && (
                                                <div className="mt-1 text-xs leading-relaxed text-zinc-600">
                                                    {t.summary}
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {tocItems.length > 0 && (
                            <section>
                                <h2 className="pp-overline mb-3">
                                    Innholdsfortegnelse
                                </h2>
                                <nav className="max-h-[60vh] overflow-y-auto pr-2">
                                    <ul className="space-y-1.5">
                                        {tocItems.map((h) => (
                                            <li key={h.id}>
                                                <a
                                                    data-testid={`toc-${h.id}`}
                                                    href={`#p-${h.id}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        const el =
                                                            document.getElementById(
                                                                `p-${h.id}`,
                                                            );
                                                        if (el) {
                                                            el.scrollIntoView({
                                                                behavior:
                                                                    "smooth",
                                                                block: "start",
                                                            });
                                                            window.history.replaceState(
                                                                null,
                                                                "",
                                                                `#p-${h.id}`,
                                                            );
                                                        }
                                                    }}
                                                    className={`block text-sm leading-snug ${
                                                        h.heading_level === 1
                                                            ? "font-semibold text-zinc-900"
                                                            : h.heading_level === 2
                                                              ? "pl-3 text-zinc-700"
                                                              : "pl-6 text-zinc-500"
                                                    } hover:text-[#C8102E]`}
                                                >
                                                    {h.text}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </nav>
                            </section>
                        )}
                    </div>
                </aside>

                {/* Main content */}
                <article
                    ref={contentRef}
                    className="md:col-span-8 lg:col-span-9"
                    data-testid="program-content"
                >
                    {renderBlocks(paragraphs, { targetId, copiedId, handleCopyLink })}
                    <StructureSourceNote source={program.structure_source} />
                </article>
            </div>
        </main>
    );
}

// Classify a paragraph document. Handles both new (kind) and legacy (is_heading) schema.
function kindOf(p) {
    if (p.is_artifact) return "artifact";
    if (p.kind) return p.kind;
    // Legacy
    if (p.is_heading) return "heading";
    return "paragraph";
}

// Group consecutive list_items into <ul>/<ol> blocks for proper semantic rendering.
function renderBlocks(paragraphs, ctx) {
    const visible = paragraphs.filter((p) => !p.is_artifact);
    const out = [];
    let i = 0;
    while (i < visible.length) {
        const p = visible[i];
        const k = kindOf(p);
        if (k === "list_item") {
            const items = [];
            const isNumbered = p.list_marker === "numbered";
            while (
                i < visible.length &&
                kindOf(visible[i]) === "list_item" &&
                Boolean(visible[i].list_marker === "numbered") === isNumbered
            ) {
                items.push(visible[i]);
                i += 1;
            }
            out.push(
                <ListBlock
                    key={items[0].id}
                    items={items}
                    ordered={isNumbered}
                    ctx={ctx}
                />,
            );
            continue;
        }
        out.push(<ParagraphBlock key={p.id} p={p} ctx={ctx} />);
        i += 1;
    }
    return out;
}

function ListBlock({ items, ordered, ctx }) {
    const ListTag = ordered ? "ol" : "ul";
    return (
        <ListTag
            data-testid={`list-${items[0].id}`}
            className={`my-3 max-w-3xl space-y-1.5 pl-6 text-[1.0625rem] leading-[1.75] text-zinc-800 ${
                ordered ? "list-decimal" : "list-disc"
            } marker:text-[#C8102E]`}
        >
            {items.map((it) => (
                <ListItem key={it.id} item={it} ctx={ctx} />
            ))}
        </ListTag>
    );
}

function ListItem({ item, ctx }) {
    const { targetId, copiedId, handleCopyLink } = ctx;
    const anchorId = `p-${item.id}`;
    const isTarget = targetId === item.id;
    const copied = copiedId === item.id;
    return (
        <li
            id={anchorId}
            className={`pp-list-item group relative ${isTarget ? "pp-target" : ""}`}
            data-testid={`paragraph-${item.id}`}
        >
            <button
                data-testid={`copy-link-${item.id}`}
                onClick={() => handleCopyLink(item.id)}
                aria-label="Kopier lenke til avsnittet"
                title="Kopier lenke til avsnittet"
                className="absolute -left-14 top-1 hidden h-7 w-7 items-center justify-center border border-zinc-200 bg-white text-zinc-500 transition-colors hover:border-[#C8102E] hover:text-[#C8102E] group-hover:flex"
            >
                {copied ? (
                    <Check className="h-4 w-4 text-[#C8102E]" />
                ) : (
                    <Hash className="h-4 w-4" />
                )}
            </button>
            <span>{item.text}</span>
        </li>
    );
}

function StructureSourceNote({ source }) {
    if (!source) return null;
    const messages = {
        tagged_pdf: {
            label: "Strukturert PDF",
            text: "Dokumentstrukturen er hentet direkte fra PDF-ens egne struktur-tagger (PDF/UA).",
        },
        visual: {
            label: "Visuell strukturanalyse",
            text: "Dokumentet har ingen struktur-tagger. Overskrifter og lister er utledet fra font-størrelse, fet skrift og repetisjon. Artefakter (sidehoder, sidetall, innholdsfortegnelse) er filtrert ut.",
        },
        ai_fallback: {
            label: "AI-generert struktur",
            text: "Dokumentet manglet både struktur-tagger og visuell overskriftsformatering. Strukturen er foreslått av AI.",
        },
    };
    const m = messages[source];
    if (!m) return null;
    return (
        <div
            data-testid="structure-source-note"
            className="mt-16 max-w-3xl border-t border-zinc-200 pt-4"
        >
            <span className="pp-overline">Strukturkilde · {m.label}</span>
            <p className="mt-2 text-xs leading-relaxed text-zinc-500">{m.text}</p>
        </div>
    );
}

function ParagraphBlock({ p, ctx }) {
    const { targetId, copiedId, handleCopyLink } = ctx;
    const anchorId = `p-${p.id}`;
    const isTarget = targetId === p.id;
    const copied = copiedId === p.id;
    const k = kindOf(p);

    if (k === "heading") {
        const level = p.heading_level || 1;
        const cls =
            level === 1
                ? "pp-heading-1"
                : level === 2
                  ? "pp-heading-2"
                  : "pp-heading-3";
        const Tag = level === 1 ? "h2" : level === 2 ? "h3" : "h4";
        return (
            <Tag
                id={anchorId}
                className={`${cls} ${isTarget ? "pp-target" : ""}`}
                data-testid={`paragraph-${p.id}`}
            >
                {p.text}
            </Tag>
        );
    }
    return (
        <div
            id={anchorId}
            className={`pp-paragraph group ${isTarget ? "pp-target" : ""}`}
            data-testid={`paragraph-${p.id}`}
        >
            <button
                data-testid={`copy-link-${p.id}`}
                onClick={() => handleCopyLink(p.id)}
                aria-label="Kopier lenke til avsnittet"
                title="Kopier lenke til avsnittet"
                className="absolute -left-8 top-3 hidden h-7 w-7 items-center justify-center border border-zinc-200 bg-white text-zinc-500 transition-colors hover:border-[#C8102E] hover:text-[#C8102E] group-hover:flex"
            >
                {copied ? <Check className="h-4 w-4 text-[#C8102E]" /> : <Hash className="h-4 w-4" />}
            </button>
            <p>{p.text}</p>
            <span className="pp-overline mt-1 inline-block text-[10px] text-zinc-400">
                s. {p.page}
            </span>
        </div>
    );
}

function KeywordPill({ keyword }) {
    const navigate = useNavigate();
    return (
        <button
            onClick={() =>
                navigate(`/search?q=${encodeURIComponent(keyword)}`)
            }
            data-testid={`keyword-${keyword}`}
            className="border border-zinc-300 bg-white px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider text-zinc-800 transition-colors hover:border-[#C8102E] hover:text-[#C8102E]"
        >
            {keyword}
        </button>
    );
}
